/**
 * Email notification service for FredoCloud
 * Uses EmailJS to send customized email templates to users
 */

import emailjs from 'emailjs-com';

// Initialize EmailJS
const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const userId = process.env.REACT_APP_EMAILJS_USER_ID;

// CC email address to include in all notification emails
const ccEmail = 'info@fredocloud.com';

/**
 * Sends a receipt notification email to the user
 * 
 * @param {Object} params - The email parameters
 * @param {string} params.userEmail - The recipient's email address
 * @param {string} params.userName - The recipient's name
 * @param {string} params.orderNumber - The order ID or reference number
 * @param {string} params.packageName - The purchased package name
 * @param {number} params.amount - The purchase amount
 * @param {string} params.receiptUrl - The URL to view the receipt
 * @returns {Promise<Object>} - The EmailJS response
 */
export const sendReceiptNotification = async (params) => {
  try {
    if (!params.userEmail) {
      throw new Error('User email is required');
    }

    const { 
      userEmail, 
      userName = 'Customer', 
      orderNumber, 
      packageName, 
      amount,
      receiptUrl
    } = params;

    // Format date for email
    const formattedDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Format currency for email
    const formattedAmount = amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });

    // Create HTML email body with clean, modern design
    const emailHtml = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9f9f9;">
        <div style="background-color: #1877f2; padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
          <h1 style="color: white; margin: 0;">Payment Receipt</h1>
          <p style="color: white; margin: 10px 0 0;">Thank you for your purchase!</p>
        </div>
        
        <div style="padding: 30px; background-color: white; border-radius: 0 0 10px 10px; box-shadow: 0 4px 8px rgba(0,0,0,0.1);">
          <p style="margin-top: 0;">Hello ${userName},</p>
          
          <p>Thank you for your recent purchase. Your payment has been processed successfully, and your receipt is now available.</p>
          
          <div style="background-color: #f5f8ff; padding: 20px; border-radius: 10px; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #333;">Order Details</h3>
            <table style="width: 100%; border-collapse: collapse;">
              <tr style="border-bottom: 1px solid #ddd;">
                <td style="padding: 10px 0; color: #666;">Order Number:</td>
                <td style="padding: 10px 0; font-weight: bold; text-align: right;">${orderNumber}</td>
              </tr>
              <tr style="border-bottom: 1px solid #ddd;">
                <td style="padding: 10px 0; color: #666;">Date:</td>
                <td style="padding: 10px 0; text-align: right;">${formattedDate}</td>
              </tr>
              <tr style="border-bottom: 1px solid #ddd;">
                <td style="padding: 10px 0; color: #666;">Package:</td>
                <td style="padding: 10px 0; text-align: right;">${packageName}</td>
              </tr>
              <tr>
                <td style="padding: 10px 0; color: #666; font-weight: bold;">Total Amount:</td>
                <td style="padding: 10px 0; font-weight: bold; text-align: right; color: #1877f2;">${formattedAmount}</td>
              </tr>
            </table>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="${receiptUrl}" target="_blank" style="display: inline-block; padding: 12px 24px; background-color: #1877f2; color: white; text-decoration: none; border-radius: 5px; font-weight: bold;">View Receipt</a>
          </div>
          
          <p>If you have any questions about your purchase or need assistance, please contact our support team at <a href="mailto:info@fredocloud.com" style="color: #1877f2;">info@fredocloud.com</a>.</p>
          
          <p style="margin-bottom: 0;">Thank you for choosing FredoCloud!</p>
          
          <p style="margin-top: 20px; font-size: 0.9em; color: #666;">Best regards,<br>The FredoCloud Team</p>
        </div>
        
        <div style="text-align: center; padding: 20px; color: #999; font-size: 0.8em;">
          <p>© ${new Date().getFullYear()} FredoCloud. All rights reserved.</p>
          <p>This is an automated message, please do not reply to this email.</p>
        </div>
      </div>
    `;

    // Prepare template parameters according to the template defined in EmailJS
    const templateParams = {
      subject: `FredoCloud Purchase Receipt: ${packageName} - Order #${orderNumber}`,
      body: emailHtml,
      mailOne: userEmail,
      mailTwo: ccEmail
    };

    console.log('Sending receipt notification email to:', userEmail);
    
    // Send the email using EmailJS
    const response = await emailjs.send(
      serviceId,
      templateId,
      templateParams,
      userId
    );

    console.log('Receipt notification email sent successfully:', response);
    return response;
  } catch (error) {
    console.error('Error sending receipt notification email:', error);
    throw error;
  }
};

/**
 * Initializes the EmailJS service
 * Call this during application startup to setup EmailJS
 */
export const initEmailService = () => {
  try {
    emailjs.init(userId);
    console.log('EmailJS initialized successfully');
  } catch (error) {
    console.error('Error initializing EmailJS:', error);
  }
};

export default {
  sendReceiptNotification,
  initEmailService
}; 